import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowRight, Sparkles, Brain, Heart, Target, Zap, 
  MessageCircle, Shield, Headphones, Mic, MicOff, 
  Loader2, Volume2, VolumeX, Play 
} from 'lucide-react';

export const LandingPageWithAudio: React.FC = () => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [audioResponse, setAudioResponse] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAudioDemo, setShowAudioDemo] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const features = [
    {
      icon: <Brain className="w-6 h-6" />,
      titleKey: 'landing.features.aiCoaching.title',
      descriptionKey: 'landing.features.aiCoaching.description'
    },
    {
      icon: <Headphones className="w-6 h-6" />,
      titleKey: 'landing.features.audioExperience.title',
      descriptionKey: 'landing.features.audioExperience.description'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      titleKey: 'landing.features.privateSecure.title',
      descriptionKey: 'landing.features.privateSecure.description'
    },
    {
      icon: <Zap className="w-6 h-6" />,
      titleKey: 'landing.features.instantSupport.title',
      descriptionKey: 'landing.features.instantSupport.description'
    }
  ];

  const categories = [
    {
      icon: <Zap className="w-8 h-8" />,
      titleKey: 'landing.categories.motivation.title',
      color: "from-orange-500 to-red-500",
      descriptionKey: 'landing.categories.motivation.description'
    },
    {
      icon: <Target className="w-8 h-8" />,
      titleKey: 'landing.categories.confidence.title',
      color: "from-blue-500 to-purple-500",
      descriptionKey: 'landing.categories.confidence.description'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      titleKey: 'landing.categories.wellness.title',
      color: "from-green-500 to-teal-500",
      descriptionKey: 'landing.categories.wellness.description'
    },
    {
      icon: <Brain className="w-8 h-8" />,
      titleKey: 'landing.categories.growth.title',
      color: "from-purple-500 to-pink-500",
      descriptionKey: 'landing.categories.growth.description'
    }
  ];

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        setIsProcessing(true);
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Here you would send the audio to your backend for processing
        // For demo purposes, we'll simulate a response
        setTimeout(async () => {
          const demoResponse = t('i18n:language') === 'pt-BR' 
            ? "Você consegue! Cada grande conquista começa com um único passo. Sua voz importa, seus objetivos são válidos e você tem tudo dentro de si para ter sucesso. Respire fundo e confie em si mesmo."
            : "You've got this! Every great achievement starts with a single step. Your voice matters, your goals are valid, and you have everything within you to succeed. Take a deep breath and trust yourself.";
          setAudioResponse(demoResponse);
          setIsProcessing(false);
          
          // Try to use ElevenLabs for better quality audio
          await playAudioResponseWithElevenLabs(demoResponse);
        }, 2000);

        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert(t('audioCoach.error'));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const playAudioResponseWithElevenLabs = async (text: string) => {
    try {
      // Determine voice based on language
      const currentLang = localStorage.getItem('i18nextLng') || 'en';
      const voice = currentLang === 'pt-BR' ? 'bella' : 'rachel'; // Use softer voices for coaching
      
      // Try to use ElevenLabs API
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/api/audio/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text, voice })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.audio_url) {
          const audio = new Audio(data.audio_url);
          audio.onplay = () => setIsPlaying(true);
          audio.onended = () => setIsPlaying(false);
          audio.onerror = () => {
            // Fallback to browser TTS if ElevenLabs fails
            playAudioResponse(text);
          };
          await audio.play();
          return;
        }
      }
    } catch (error) {
      console.log('ElevenLabs not available, using browser TTS');
    }
    
    // Fallback to browser TTS
    playAudioResponse(text);
  };

  const playAudioResponse = (text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      
      // Set language based on current i18n language
      const currentLang = localStorage.getItem('i18nextLng') || 'en';
      utterance.lang = currentLang === 'pt-BR' ? 'pt-BR' : 'en-US';
      
      utterance.onstart = () => setIsPlaying(true);
      utterance.onend = () => setIsPlaying(false);
      
      speechSynthesis.speak(utterance);
    }
  };

  const stopAudio = () => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
      setIsPlaying(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Audio Demo */}
      <section className="relative overflow-hidden py-20 sm:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-transparent to-pink-600/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center p-2 bg-purple-500/10 rounded-full mb-8">
              <Sparkles className="w-8 h-8 text-purple-400" />
            </div>
            
            <h1 className="text-5xl sm:text-7xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
              {t('landing.hero.title')}
              <br />
              {t('landing.hero.subtitle')}
            </h1>
            
            <p className="text-xl sm:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto">
              {t('landing.hero.description')}
            </p>

            {/* Quick Audio Demo */}
            {!showAudioDemo ? (
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link
                  to="/record"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-xl text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-105 shadow-xl"
                >
                  <Mic className="mr-2 w-5 h-5" />
                  {t('landing.hero.startSession')}
                </Link>
                
                <button
                  onClick={() => setShowAudioDemo(true)}
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-xl text-white bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all"
                >
                  <MessageCircle className="mr-2 w-5 h-5" />
                  {t('landing.hero.tryDemo')}
                </button>
              </div>
            ) : (
              <div className="max-w-2xl mx-auto mb-8 p-6 bg-black/30 backdrop-blur-sm rounded-2xl border border-white/10">
                <div className="flex flex-col items-center gap-4">
                  {!audioResponse ? (
                    <>
                      <p className="text-gray-300 mb-2">
                        {isProcessing ? t('audioCoach.recording.processing') : t('audioCoach.input.label')}
                      </p>
                      <button
                        onClick={isRecording ? stopRecording : startRecording}
                        disabled={isProcessing}
                        className={`flex items-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all ${
                          isRecording 
                            ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' 
                            : isProcessing
                            ? 'bg-gray-500 text-white cursor-not-allowed'
                            : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white'
                        }`}
                      >
                        {isProcessing ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            {t('audioCoach.recording.processing')}
                          </>
                        ) : isRecording ? (
                          <>
                            <MicOff className="w-5 h-5" />
                            {t('audioCoach.recording.stopRecording')}
                          </>
                        ) : (
                          <>
                            <Mic className="w-5 h-5" />
                            {t('audioCoach.recording.startRecording')}
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <div className="w-full">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="flex-shrink-0">
                          {isPlaying ? (
                            <Volume2 className="w-6 h-6 text-purple-400 animate-pulse" />
                          ) : (
                            <Play className="w-6 h-6 text-purple-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-300 text-left">{audioResponse}</p>
                        </div>
                      </div>
                      <div className="flex gap-3 justify-center">
                        {isPlaying ? (
                          <button
                            onClick={stopAudio}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                          >
                            <VolumeX className="w-5 h-5" />
                            {t('audioCoach.input.stopAudio')}
                          </button>
                        ) : (
                          <button
                            onClick={() => playAudioResponseWithElevenLabs(audioResponse)}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                          >
                            <Volume2 className="w-5 h-5" />
                            {t('audioCoach.response.playAgain')}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setAudioResponse('');
                            setShowAudioDemo(false);
                          }}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                        >
                          Close Demo
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <p className="text-sm text-gray-400">
              {t('landing.hero.freeToStart')}
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-black/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {t(feature.titleKey)}
                </h3>
                <p className="text-gray-400">
                  {t(feature.descriptionKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('landing.categories.title')}
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t('landing.categories.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <Link
                key={index}
                to={`/coach?category=${t(category.titleKey).toLowerCase()}`}
                className="group relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-8 hover:bg-white/10 transition-all transform hover:scale-105"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-r ${category.color} mb-4`}>
                  {category.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  {t(category.titleKey)}
                </h3>
                <p className="text-gray-400">
                  {t(category.descriptionKey)}
                </p>
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight className="w-5 h-5 text-purple-400" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-900/50 to-pink-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {t('landing.cta.title')}
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            {t('landing.cta.subtitle')}
          </p>
          <Link
            to="/coach"
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-xl text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-105 shadow-xl"
          >
            {t('landing.cta.button')}
            <Sparkles className="ml-2 w-5 h-5" />
          </Link>
          <p className="mt-6 text-sm text-gray-400">
            {t('landing.cta.footer')}
          </p>
        </div>
      </section>
    </div>
  );
};